import { Signal } from "@engine";
import {
  DEFAULT_DIFFICULTY,
  profileById,
  type DifficultyId,
  type DifficultyProfile,
} from "../config/difficulty";

const STORAGE_KEY = "morph.difficulty.v1";

// Owns the player's currently-selected difficulty. Persists to
// localStorage so the choice survives reloads. MorphHexGame reads
// getProfile() at startGame to wire the speed/score/last-chance/risk
// multipliers; the menu HUD calls setLevel(id) on user click.
//
// Difficulty changes mid-run are deliberately NOT supported (we only
// re-read at startGame), so the pill row in the menu doesn't need to
// hot-swap state across an active streamer/score session.
export class DifficultyService {
  readonly onChanged = new Signal<DifficultyId>();

  private current: DifficultyId = DEFAULT_DIFFICULTY;

  constructor() {
    this.load();
  }

  getId(): DifficultyId {
    return this.current;
  }

  getProfile(): DifficultyProfile {
    return profileById(this.current);
  }

  // Idempotent setter. No-op if the requested id is already active so
  // the pill row click handler can naively re-emit on re-render.
  setLevel(id: DifficultyId): void {
    if (this.current === id) return;
    this.current = id;
    this.persist();
    this.onChanged.emit(id);
  }

  private load(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      // Defensive: an unknown id (future schema bump) silently falls
      // back to default so the player isn't stuck on a non-existent
      // profile.
      if (raw === "easy" || raw === "normal" || raw === "hard") {
        this.current = raw;
      }
    } catch {
      // ignore quota / privacy errors
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, this.current);
    } catch {
      // ignore storage errors
    }
  }
}
